import { AttachmentDetailsMetadata } from "../../models/AttachmentDetailsMetadata";
import { WorkflowActionAttachment } from "../../models/workflow/WorkflowActionAttachment";
import { parseProjectNrn } from "../NrnHelpers";

/**
 * Server-side project folder files do not need a real byte size in the
 * workflow attachment payload — the server already has the file and ignores
 * this field when isFromProjectFolder=true.  Use 1 as a non-zero placeholder
 * so any downstream > 0 guards still pass.
 */
const PROJECT_FILE_SIZE_PLACEHOLDER = 1;

export function mapAttachmentsMetadataToAttachment(
    attachmentsDetails: AttachmentDetailsMetadata[],
    isLastModifiedDateSupported?: boolean,
    projectNrn?: string
): WorkflowActionAttachment[] {
    // Parse the project-level projectId from the project NRN. If the NRN is
    // malformed (e.g. test mocks or an unexpected server format), fall back to
    // undefined — each attachment then uses its own projectId instead.
    let parsedProjectId: string | undefined;
    try {
        parsedProjectId = projectNrn ? parseProjectNrn(projectNrn).projectId : undefined;
    } catch {
        parsedProjectId = undefined;
    }
    if (attachmentsDetails?.length > 0) {
        if (isLastModifiedDateSupported) {
            return attachmentsDetails.map((x) => ({
                fileName: x.fileName,
                sizeInBytes: x.isFromProjectFolder ? PROJECT_FILE_SIZE_PLACEHOLDER : x.file.size,
                contentType: x.file.type || "application/octet-stream",
                lastModifiedDate: x.lastModifiedDate,
                ...(x.isFromProjectFolder && {
                    isFromProjectFolder: true,
                    addToParentFolder: x.addToParentFolder,
                    // Use the per-file projectId (derived from the project NRN at
                    // selection time) so files from different projects carry the correct
                    // projectId. Fall back to parsedProjectId (transfer-level project).
                    projectId: x.projectId ?? parsedProjectId,
                    projectFolderRelativePath: x.projectFolderRelativePath,
                    folderId: x.folderId,
                }),
            }));
        } else {
            return attachmentsDetails.map((x) => ({
                fileName: x.fileName,
                sizeInBytes: x.isFromProjectFolder ? PROJECT_FILE_SIZE_PLACEHOLDER : x.file.size,
                contentType: x.file.type || "application/octet-stream",
                ...(x.isFromProjectFolder && {
                    isFromProjectFolder: true,
                    projectId: x.projectId ?? parsedProjectId,
                    addToParentFolder: x.addToParentFolder,
                    projectFolderRelativePath: x.projectFolderRelativePath,
                    folderId: x.folderId,
                }),
            }));
        }
    } else {
        return [];
    }
}
